<template>
    <CqModal
        :showModal="show"
        heading="Medewerker selecteren"
        :disableCloseButton="saving"
        minWidth="40rem"
        maxHeight="90vh"
        @closeModal="closeModal"
    >
        <div class="employeeSelectModal">
            <div class="addSection">
                <CqButton
                    label="Nieuwe medewerker invoeren"
                    icon="plus"
                    @click="openNewCompanyEmployeeModal"
                />
            </div>
            <CqFilterPanel class="filterPanel" :filterCounter="search !== '' ? 1 : 0">
                <CqTextInput
                    label="Zoeken"
                    v-model="search"
                    placeholder="Zoek op naam, e-mail of telefoon"
                    :autoFocusShortcut="{ctrlOrMeta: true, key: 'f'}"
                />
            </CqFilterPanel>

            <CqList
                v-model="listHeaders"
                class="results"
                :status="listStatus"
                :columnDefinition="{wide: 'repeat(3, 1fr) min-content'}"
                rowHeight="withButtons"
                sticky="1.5rem"
                sortable
            >
                <div class="infoResult" v-if="search === ''">
                    Begin met zoeken naar een medewerker.
                </div>
                <template v-else-if="sortedSearchResults.length > 0">
                    <template v-for="employee in sortedAndLimitedSearchResults" :key="employee.id">
                        <div class="name">{{ employee.name }}</div>
                        <div class="phone">{{ employee.phone.length ? employee.phone : '-' }}</div>
                        <div class="email">{{ employee.email.length ? employee.email : '-' }}</div>
                        <div class="action">
                            <CqButton
                                primary
                                label="Selecteer"
                                :disabled="isAlreadyAssigned(employee) || saving"
                                @click="selectEmployee(employee)"
                                margin="no-top-bottom"
                            />
                        </div>
                    </template>
                    <div class="infoResult" v-if="sortedSearchResults.length > maxVisibleSearchResults">
                        Er zijn meer dan {{ maxVisibleSearchResults }} resultaten gevonden. Verfijn uw zoekopdracht.
                    </div>
                </template>
            </CqList>
        </div>
        <CompanyEmployeeCreateModal
            v-model="newEmployee"
            :company="company"
            :isPending="saving || loading"
            @created:employee="createdAndSelectEmployee"
            @closeModal="closeNewCompanyEmployeeModal"
        />
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Watch} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqList, CqListHeaderDefinition, CqModal, CqTextInput, CqListStatus, CqFilterPanel, CqListUtil} from '@cquential/ui';
    import {ApiClient, CompanyEmployee} from '@cquential/api';
    import {CompanyWithDeploymentsAndEmployees} from '@/types/api-object-extensions';
    import Notify from '@/components/modals/Notify.vue';
    import CompanyEmployeeCreateModal from '@/components/modals/CompanyEmployeeCreateModal.vue';

    @Component({
        components: {
            CompanyEmployeeCreateModal,
            CqButton,
            CqButtonBar,
            CqList,
            CqModal,
            CqTextInput,
            CqFilterPanel,
        },
        emits: ['addEmployeeToCompany'],
    })
    export default class CompanyEmployeeSelectModal extends Vue {
        public static readonly SEARCH_WAIT_TIMER: number = 500;
        private static instance: CompanyEmployeeSelectModal | null = null;

        public listHeaders: CqListHeaderDefinition<CompanyEmployee>[] = [
            {label: 'Naam', identifier: 'name', sortPriority: 1, currentSort: 'ASC'},
            {label: 'Telefoon', identifier: 'phone'},
            {label: 'Email', identifier: 'email'},
            {label: '', unsortable: true},
        ];

        public show: boolean = false;
        public company: CompanyWithDeploymentsAndEmployees | null = null;
        public search: string = '';
        public searchResults: CompanyEmployee[] = [];
        public loading: boolean = false;
        public saving: boolean = false;
        public newEmployee: CompanyEmployee | null = null;

        private searchDelayTimer: ReturnType<typeof setTimeout> | null = null;
        public maxVisibleSearchResults: number = 200;

        private get listStatus(): CqListStatus {
            if (this.loading) {
                return 'loading';
            } else if (this.search !== '' && this.searchResults.length === 0) {
                return 'empty';
            }

            return undefined;
        }

        public get sortedSearchResults(): CompanyEmployee[] {
            return CqListUtil.sortData(this.searchResults, this.listHeaders);
        }

        public get sortedAndLimitedSearchResults(): CompanyEmployee[] {
            return this.sortedSearchResults.slice(0, this.maxVisibleSearchResults);
        }

        public mounted(): void {
            CompanyEmployeeSelectModal.instance = this;
        }

        public beforeUnmount(): void {
            CompanyEmployeeSelectModal.instance = null;
        }

        public static open(company: CompanyWithDeploymentsAndEmployees): void {
            this.instance!.company = company;
            this.instance!.resetState();
            this.instance!.show = true;
        }

        @Watch('search')
        private onSearchQueryChanged(): void {
            if (this.searchDelayTimer) {
                clearTimeout(this.searchDelayTimer);
            }

            if (this.search === '') {
                this.searchResults = [];

                return;
            }

            this.searchDelayTimer = setTimeout((): void => {
                this.loadEmployees();
            }, CompanyEmployeeSelectModal.SEARCH_WAIT_TIMER);
        }

        private isAlreadyAssigned(employee: CompanyEmployee): boolean {
            if (!this.company) {
                return false;
            }

            return Object.values(this.company.employees).some(
                (companyEmployee: CompanyEmployee): boolean => companyEmployee.id === employee.id,
            );
        }

        private async loadEmployees(): Promise<void> {
            if (!this.company?.openShiftMarketplaceSyncEnabled) {
                return;
            }

            this.loading = true;
            try {
                this.searchResults = await ApiClient.instance.fetchCompanyEmployees(this.company.id!, this.search);
            } catch {
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Fout bij het zoeken naar medewerkers.', type: 'error'});
            } finally {
                this.loading = false;
            }
        }

        private async selectEmployee(employee: CompanyEmployee): Promise<void> {
            if (!this.company?.openShiftMarketplaceSyncEnabled) {
                return;
            }

            this.$emit('addEmployeeToCompany', this.company, {
                ...employee,
                companyId: this.company.id,
                deployments: {},
            });

            this.closeModal();
        }

        private openNewCompanyEmployeeModal(): void {
            this.newEmployee = {
                initials: '',
                firstName: '',
                firstNames: '',
                namePrefixes: '',
                lastName: '',
                name: '',
                email: '',
                phone: '+31',
                companyId: this.company!.id!,
                company: this.company!,
            };
        }

        public async createdAndSelectEmployee(employee: CompanyEmployee): Promise<void> {
            this.loading = true;
            try {
                await this.selectEmployee(await ApiClient.instance.fetchCompanyEmployee(employee.companyId!, employee.id!));
            } catch (error: unknown) {
                console.error(error);
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Er ging iets mis met het ophalen van de medewerker', type: 'error'});
            } finally {
                this.loading = false;
            }
            this.closeNewCompanyEmployeeModal();
        }

        public closeNewCompanyEmployeeModal(): void {
            this.newEmployee = null;
        }

        private closeModal(): void {
            this.show = false;
            if (this.searchDelayTimer) {
                clearTimeout(this.searchDelayTimer);
            }
        }

        private resetState(): void {
            this.search = '';
            this.searchResults = [];
            this.loading = false;
            this.saving = false;
            this.newEmployee = null;
        }
    }
</script>

<style lang="scss" scoped>
    .employeeSelectModal {
        .results {
            .name,
            .phone,
            .email,
            .action {
                padding: 0 .25rem;
                height: var(--cq-list-row-height-buttons);
                display: flex;
                align-items: center;
            }

            .startSearch {
                grid-column: 1 / -1;
                justify-self: center;
                padding: .25rem;
            }
        }

        .name {
            font-weight: bold;
        }

        .action {
            text-align: right;
        }

        .addSection {
            display: flex;
            justify-content: flex-end;
        }
    }
</style>
